import { useEffect, useRef, useState } from 'preact/hooks';
import type { EpisodeDto, FeedDto } from '../../shared/types';
import { api, ApiError } from '../api';
import { Artwork } from '../components/Artwork';
import { EpisodeRow } from '../components/EpisodeRow';
import { Icon } from '../components/Icon';
import { SkeletonEpisodeList, SkeletonHeader } from '../components/Skeleton';
import { Spinner } from '../components/Spinner';
import { announce } from '../lib/announce';
import { sanitizeShowNotesHtml } from '../lib/sanitize';
import { isSignedIn } from '../state/session';

export function Podcast({ id }: { id: string }) {
  const feedId = Number.parseInt(id, 10);
  const [feed, setFeed] = useState<FeedDto | null>(null);
  const [episodes, setEpisodes] = useState<EpisodeDto[]>([]);
  const [cursor, setCursor] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showAllNotes, setShowAllNotes] = useState(false);

  // The route does not key the component by id, so navigating between shows reuses this
  // same instance — only `feedId` changes. `loadMore` and `refresh` are user-triggered and
  // outlive any single render, so they read `feedIdRef` (always current) rather than the
  // `feedId` closed over when they were called, to tell whether the user has since moved on
  // to a different show. `controllerRef` holds the in-flight AbortController for "requests
  // belonging to the show currently on screen" — every request tied to this view, including
  // ones `loadMore`/`refresh` start later, shares it, so navigating away aborts all of them.
  const feedIdRef = useRef(feedId);
  feedIdRef.current = feedId;
  const controllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    controllerRef.current = controller;
    setLoading(true);
    setLoadingMore(false);
    setBusy(false);
    setError(null);
    api
      .feed(feedId, { signal: controller.signal })
      .then((res) => {
        if (controller.signal.aborted) return;
        setFeed(res.feed);
        setEpisodes(res.episodes);
        // A null cursor means the first page was the whole show — no "load more" button.
        setCursor(res.nextCursor);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError(err instanceof ApiError ? err.message : 'Could not load the show.');
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        setLoading(false);
      });
    return () => {
      controller.abort();
    };
  }, [feedId]);

  const loadMore = async () => {
    if (cursor === null) return;
    const requestId = feedId;
    setLoadingMore(true);
    try {
      const res = await api.feedEpisodes(feedId, cursor, undefined, {
        signal: controllerRef.current?.signal,
      });
      // The user may have navigated to a different show while this was in flight — don't
      // append its episodes onto whatever is on screen now.
      if (feedIdRef.current !== requestId) return;
      setEpisodes((prev) => [...prev, ...res.episodes]);
      setCursor(res.nextCursor);
    } catch {
      if (feedIdRef.current === requestId) setCursor(null);
    } finally {
      if (feedIdRef.current === requestId) setLoadingMore(false);
    }
  };

  /*
   * Optimistic: the button flips on the press, not on the response.
   *
   * Subscribing is a binary the server has no opinion about beyond "yes" — there is nothing
   * to wait for and nothing to reconcile. So the label changes immediately and the request
   * settles behind it; a failure puts the old state back and says so out loud, which is the
   * only case where the network was worth the listener's attention. The button is not
   * disabled during the round trip either, because a disabled control that re-enables a
   * moment later is what makes the old version feel slow.
   */
  const toggleSubscription = async () => {
    if (!feed) return;
    const wasSubscribed = feed.subscribed;
    const next = !wasSubscribed;
    setFeed({ ...feed, subscribed: next });

    try {
      await (wasSubscribed ? api.unsubscribe(feed.id) : api.subscribe(feed.id));
    } catch {
      setFeed((current) => (current ? { ...current, subscribed: wasSubscribed } : current));
      announce(
        wasSubscribed
          ? `Could not unsubscribe from ${feed.title}.`
          : `Could not subscribe to ${feed.title}.`,
      );
      return;
    }

    announce(next ? `Subscribed to ${feed.title}.` : `Unsubscribed from ${feed.title}.`);
  };

  const refresh = async () => {
    const requestId = feedId;
    const signal = controllerRef.current?.signal;
    setBusy(true);
    try {
      await api.refreshFeed(feedId, { signal });
      const res = await api.feed(feedId, { signal });
      // Dropped if the user has since navigated to a different show — otherwise this would
      // replace the new show's content with the old one's.
      if (feedIdRef.current !== requestId) return;
      setFeed(res.feed);
      setEpisodes(res.episodes);
      setCursor(res.nextCursor);
    } catch {
      /* the refresh is best effort */
    } finally {
      if (feedIdRef.current === requestId) setBusy(false);
    }
  };

  // A skeleton in the shape of the page, rather than a spinner that is replaced by content
  // of a completely different height — the swap is a cross-fade instead of a jump.
  if (loading) {
    return (
      <div class="view">
        <SkeletonHeader label="Loading show" />
        <SkeletonEpisodeList count={5} />
      </div>
    );
  }
  if (error || !feed) return <p class="error">{error ?? 'Show not found.'}</p>;

  return (
    <div class="view">
      <header class="show-header">
        <Artwork src={feed.imageUrl} alt="" size={180} className="show-header__art" />
        <div class="show-header__body">
          <h1>{feed.title}</h1>
          {feed.author && <p class="show-header__author">{feed.author}</p>}

          <p class="show-header__badges">
            {feed.itunesType === 'serial' && <span class="badge">Serial</span>}
            {feed.complete && <span class="badge">Completed</span>}
            {feed.explicit && <span class="badge badge--warn">Explicit</span>}
            {feed.categories.slice(0, 3).map((cat) => (
              <span key={cat} class="badge badge--muted">
                {cat}
              </span>
            ))}
            {feed.pc20Phases.length > 0 && (
              <span class="badge badge--muted" title="Podcasting 2.0 phases this feed uses">
                PC2.0 · {feed.pc20Phases.join(', ')}
              </span>
            )}
          </p>

          <div class="show-header__actions">
            {isSignedIn.value && (
              <button
                type="button"
                class={`button ${feed.subscribed ? '' : 'button--primary'}`}
                onClick={toggleSubscription}
                aria-pressed={feed.subscribed}
              >
                {feed.subscribed ? 'Unsubscribe' : 'Subscribe'}
              </button>
            )}
            {isSignedIn.value && (
              <button
                type="button"
                class="icon-button"
                onClick={refresh}
                disabled={busy}
                aria-label="Check for new episodes"
                title="Check for new episodes"
              >
                <Icon name="refresh" size={20} />
              </button>
            )}
            {feed.link && (
              <a class="button button--ghost" href={feed.link} target="_blank" rel="external noopener noreferrer">
                Website
              </a>
            )}
          </div>

          {feed.funding.length > 0 && (
            <p class="show-header__funding">
              {feed.funding.map((f) => (
                <a key={f.url} class="chip" href={f.url} target="_blank" rel="external noopener noreferrer">
                  {f.label || 'Support this show'}
                </a>
              ))}
            </p>
          )}

          {feed.description && (
            <div class={`prose show-header__description ${showAllNotes ? '' : 'is-clamped'}`}>
              {/* Publisher HTML, sanitized to a safe subset — see src/client/lib/sanitize.ts. */}
              <div dangerouslySetInnerHTML={{ __html: sanitizeShowNotesHtml(feed.description) }} />
            </div>
          )}
          {feed.description && feed.description.length > 300 && (
            <button
              type="button"
              class="link-button"
              onClick={() => setShowAllNotes((v) => !v)}
              aria-expanded={showAllNotes}
            >
              {showAllNotes ? 'Show less' : 'Show more'}
            </button>
          )}

          {feed.persons.length > 0 && (
            <ul class="credits">
              {feed.persons.slice(0, 8).map((person) => (
                <li key={`${person.name}-${person.role ?? ''}`} class="credits__item">
                  {person.img && <Artwork src={person.img} alt="" size={32} />}
                  <span>
                    <a href={`/person/${encodeURIComponent(person.name)}`} class="credits__link">
                      <strong>{person.name}</strong>
                    </a>
                    {person.role && <span class="muted"> · {person.role}</span>}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </header>

      <section class="section">
        <h2 class="section__title">Episodes</h2>
        <div class="episode-list">
          {episodes.map((episode) => (
            <EpisodeRow key={episode.id} episode={episode} showArtwork={false} />
          ))}
        </div>

        {cursor !== null && (
          <button type="button" class="button button--ghost" onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? <><Spinner /> Loading…</> : 'Load older episodes'}
          </button>
        )}
      </section>
    </div>
  );
}
